import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Stack, alpha } from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import SvgColor from 'src/components/svg-color';
import countriesJson from 'src/utils/countries.json';
import CustomTooltip from '../components/CustomTooltip';
import Iconify from '../../iconify';
import CompetitionEvolutionCell from '../components/CompetitionEvolutionCell';
import HoverShortcut from '../components/HoverShortcut';
import { ResponsiveConfig } from './use-responsive-config';
import { domainFavicon } from '../utils';
import { PresenceSegments } from '../types';


const getPresenceTone = (rate?: number) => {
    if (typeof rate !== 'number') return 'warning';
    if (rate < 20) return 'danger';
    if (rate < 40) return 'warning';
    return 'success';
};

const TopCompetitorPillContent = ({
    domain,
    rate,
    iconSize,
    fontSize,
    onClick,
}: {
    domain: string;
    rate?: number;
    iconSize: number;
    fontSize: number;
    onClick?: (event: React.MouseEvent) => void;
}) => {
    const tone = getPresenceTone(rate);

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                pl: 1,
                pr: 1,
                py: 0.25,
                borderRadius: '32px',
                backgroundColor: 'rgba(145, 158, 171, 0.1)',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'background-color 0.15s',
                '&:hover': onClick ? {
                    backgroundColor: 'rgba(0, 108, 156, 0.08)',
                    '& .pill-domain-label': {
                        color: '#006C9C',
                        textDecoration: 'underline',
                    },
                } : undefined,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, gap: 0.5 }}>
                <Box
                    component="img"
                    src={domainFavicon(domain)}
                    sx={{
                        width: iconSize,
                        height: iconSize,
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                        display: 'block',
                    }}
                />
                <Typography
                    className="pill-domain-label"
                    variant="body2"
                    sx={{
                        fontSize,
                        lineHeight: '22px',
                        color: '#333C47',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        transition: 'color 0.15s',
                    }}
                >
                    {domain}
                </Typography>
            </Box>
        </Box>
    );
};

const TopCompetitorPill = ({
    domain,
    rate,
    iconSize,
    fontSize,
    countryName,
}: {
    domain: string;
    rate?: number;
    iconSize: number;
    fontSize: number;
    countryName?: string;
}) => {
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        const countryEntry = countriesJson?.find((c: any) => c.name === countryName);
        const code = countryEntry?.code?.toUpperCase() || '';
        navigate(`/visibility/ads?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(domain)}`);
    };

    return (
        <TopCompetitorPillContent
            domain={domain}
            rate={rate}
            iconSize={iconSize}
            fontSize={fontSize}
            onClick={handleClick}
        />
    );
};

const getVisibleCount = (widths: number[], gap: number, availableWidth: number) => {
    let used = 0;
    let count = 0;
    widths.forEach((width) => {
        const next = count === 0 ? width : width + gap;
        if (used + next <= availableWidth + 0.5) {
            used += next;
            count += 1;
        }
    });
    return count;
};

const TopCompetitorsCell = ({
    competitors,
    rates,
    responsiveConfig,
    countryName,
    t,
}: {
    competitors: string[];
    rates: Record<string, number>;
    responsiveConfig: ResponsiveConfig;
    countryName?: string;
    t: (key: string) => string;
}) => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLDivElement | null>(null);
    const [visibleCount, setVisibleCount] = useState(competitors.length);
    const [truncatedCount, setTruncatedCount] = useState(0);
    const [hovered, setHovered] = useState(false);

    const handleSeeEvolutionClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const countryEntry = countriesJson?.find((c) => c.name === countryName);
        const code = countryEntry?.code?.toUpperCase() || '';
        navigate(`/visibility/competition-evolution?country=${encodeURIComponent(code)}`);
    };

    const pillIconSize = responsiveConfig.iconSize.domain + 5;
    const fontSize = responsiveConfig.fontSize.body;

    const competitorKey = useMemo(() => competitors.join('|'), [competitors]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const compute = () => {
            const children = Array.from(measure.children) as HTMLElement[];
            const styles = window.getComputedStyle(measure);
            const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
            const widths = children.map((child) => child.getBoundingClientRect().width);
            const totalCount = widths.length;
            if (totalCount === 0) {
                setVisibleCount(0);
                return;
            }

            const containerWidth = container.getBoundingClientRect().width;
            const fitWithoutOverlay = getVisibleCount(widths, gap, containerWidth);
            if (fitWithoutOverlay >= totalCount) {
                setVisibleCount(totalCount);
                setTruncatedCount(0);
                return;
            }

            const overlayWidth = 35;
            const fitWithOverlay = getVisibleCount(widths, gap, Math.max(0, containerWidth - overlayWidth));
            const allowOverflowCount = Math.min(totalCount, fitWithOverlay + 1);
            setVisibleCount(Math.max(1, allowOverflowCount));
            setTruncatedCount(Math.max(0, totalCount - fitWithOverlay));
        };

        compute();
        const observer = new ResizeObserver(compute);
        observer.observe(container);
        return () => observer.disconnect();
    }, [competitorKey, pillIconSize, fontSize]);

    const visibleCompetitors = competitors.slice(0, visibleCount);
    const remainingCount = Math.max(0, truncatedCount);

    return (
        <Box
            ref={containerRef}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                mr: -2,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minWidth: 0,
                    overflow: 'hidden',
                    pr: 0,
                }}
            >
                {visibleCompetitors.map((competitor: string) => (
                    <TopCompetitorPill
                        key={competitor}
                        domain={competitor}
                        rate={rates[competitor]}
                        iconSize={pillIconSize}
                        fontSize={fontSize}
                        countryName={countryName}
                    />
                ))}
            </Box>
            {hovered ? (
                <Box
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        pl: 3,
                        background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 20%)',
                        backdropFilter: 'blur(2px)',
                    }}
                >
                    <HoverShortcut
                        defaultContent=""
                        hoverContent={
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                {t('dashboard.global.visibility.seeEvolution')}
                                <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
                            </Box>
                        }
                        onClick={handleSeeEvolutionClick}
                        forceHovered
                        fontSize={responsiveConfig.fontSize.body}
                        sx={{ ml: 0 }}
                    />
                </Box>
            ) : remainingCount > 0 ? (
                <Box
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        scale: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        p: 0,
                        backdropFilter: 'blur(1px)',
                        backgroundColor: 'rgba(240, 240, 240, 0.7)',
                        pointerEvents: 'none',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 7,
                            lineHeight: '22px',
                            color: '#2F3843',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {`+${remainingCount}`}
                    </Typography>
                </Box>
            ) : null}
            <Box
                ref={measureRef}
                sx={{
                    position: 'absolute',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    height: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                {competitors.map((competitor: string) => (
                    <TopCompetitorPillContent
                        key={`measure-${competitor}`}
                        domain={competitor}
                        rate={rates[competitor]}
                        iconSize={pillIconSize}
                        fontSize={fontSize}
                    />
                ))}
            </Box>
        </Box>
    );
};

const VisibilityRateCell = ({
    row,
    responsiveConfig,
    PresenceRateChart,
    selectedCountry,
    columnType,
    t,
}: {
    row: any;
    responsiveConfig: ResponsiveConfig;
    PresenceRateChart: React.ComponentType<{ data: PresenceSegments; height?: number; totalVisibilityRate?: number }>;
    selectedCountry?: string | null;
    columnType?: 'country' | 'searchTerm';
    t: (key: string) => string;
}) => {
    const navigate = useNavigate();
    const [hovered, setHovered] = useState(false);
    const countryName = columnType === 'country' ? row?.country : (selectedCountry || row?.country);
    const value = row?.blendedPresenceRate;

    const handleSeeAdsClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const countryEntry = countriesJson?.find((c) => c.name === countryName);
        const code = countryEntry?.code?.toUpperCase() || '';
        const brandDomain = row?.brandDomain;
        const url = `/visibility/ads?country=${encodeURIComponent(code)}${brandDomain ? `&domain=${encodeURIComponent(brandDomain)}` : ''}`;
        navigate(url);
    };

    const hoverLabelSeeAds = (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            {t('dashboard.global.visibility.seeYourAds')}
            <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
        </Box>
    );

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.padding, width: '100%' }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 'medium',
                    minWidth: responsiveConfig.iconSize.flag + 28,
                    maxWidth: responsiveConfig.iconSize.flag + 28,
                    textAlign: 'right',
                    fontSize: responsiveConfig.fontSize.body,
                }}
            >
                {typeof value === 'number' && !isNaN(value) ? Math.round(value) : 0}%
            </Typography>
            <Box sx={{ position: 'relative', flex: '1 1 0', minWidth: 0, width: 0, height: 40, display: 'flex', alignItems: 'center' }}>
                <PresenceRateChart data={row?.presenceRateData} height={40} totalVisibilityRate={value} />
                {hovered && (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            pl: 3,
                            pr: 0.5,
                            background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 20%)',
                            backdropFilter: 'blur(2px)',
                        }}
                    >
                        <HoverShortcut
                                defaultContent=""
                                hoverContent={hoverLabelSeeAds}
                                onClick={handleSeeAdsClick}
                                forceHovered
                                fontSize={responsiveConfig.fontSize.body}
                                sx={{ ml: 0 }}
                            />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const countryCodeToLanguage: Record<string, string> = {
    gb: 'en', us: 'en', ca: 'en', au: 'en', nz: 'en', ie: 'en',
    za: 'en', in: 'en', sg: 'en', ph: 'en', my: 'en',
    br: 'pt', at: 'de', ch: 'de', be: 'fr', mx: 'es',
    ar: 'es', cl: 'es', co: 'es', pe: 'es',
};

interface DataGridConfigProps {
    responsiveConfig: ResponsiveConfig;
    theme: any;
    t: (key: string) => string;
    workspace?: any;
    sortModel: GridSortModel;
    toggleTrafficShareSort: () => void;
    PresenceRateChart: React.ComponentType<{ data: PresenceSegments; height?: number; totalVisibilityRate?: number }>;
    columnType?: 'country' | 'searchTerm';
    cellMode?: 'overview' | 'searchTermAnalysis';
    selectedCountry?: string | null;
}

const SearchTermCell = ({
    row,
    responsiveConfig,
    cellMode = 'searchTermAnalysis',
    selectedCountry,
    t,
}: {
    row: any;
    responsiveConfig: ResponsiveConfig;
    cellMode?: 'overview' | 'searchTermAnalysis';
    selectedCountry?: string | null;
    t: (key: string) => string;
}) => {
    const navigate = useNavigate();
    const term = row?.searchTerm;
    const ts = typeof row?.trafficShare === 'number' ? `${Math.round(row.trafficShare)}%` : '-';

    const handleHoverClick = (e: React.MouseEvent) => {
        if (cellMode === 'overview') {
            const countryName = row?.country || selectedCountry;
            const countryEntry = countriesJson?.find((c) => c.name === countryName);
            const code = countryEntry?.code?.toUpperCase() || '';
            const stPath = `/visibility/st-analysis?country=${encodeURIComponent(code)}&q=${encodeURIComponent(term || '')}`;
            navigate(stPath);
        } else {
            const countryName = row?.country || selectedCountry;
            const countryEntry = countriesJson?.find((c) => c.name === countryName);
            const cc = countryEntry?.code?.toLowerCase() || '';
            const hl = countryCodeToLanguage[cc] || cc;
            window.open(
                `https://www.google.com/search?q=${encodeURIComponent(term || '')}&gl=${cc}&hl=${hl}`,
                '_blank',
            );
        }
    };

    const hoverLabel = (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            {cellMode === 'overview' ? t('dashboard.global.visibility.seeST') : t('dashboard.global.visibility.search')}
            <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <CustomTooltip title={term ?? ''} placement="top">
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: responsiveConfig.fontSize.body,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {term}
                </Typography>
            </CustomTooltip>
            <HoverShortcut
                defaultContent={ts}
                hoverContent={hoverLabel}
                onClick={handleHoverClick}
                fontSize={responsiveConfig.fontSize.body}
            />
        </Box>
    );
};

const CountryCell = ({
    row,
    responsiveConfig,
    t,
}: {
    row: any;
    responsiveConfig: ResponsiveConfig;
    t: (key: string) => string;
}) => {
    const navigate = useNavigate();
    const [hovered, setHovered] = useState(false);
    const countryName = row?.country;
    const country = countriesJson?.find((c) => c.name === countryName);
    const flagCode = country ? country.code : null;
    const ts = typeof row?.trafficShare === 'number' ? `${Math.round(row.trafficShare)}%` : '-';

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const code = country?.code?.toUpperCase() || '';
        navigate(`/visibility/st-analysis?country=${encodeURIComponent(code)}`);
    };

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.column, minWidth: 0 }}>
                {flagCode ? (
                    <FlagIcon
                        code={flagCode}
                        sx={{ width: responsiveConfig.iconSize.flag, height: responsiveConfig.iconSize.flag, borderRadius: '50%', flexShrink: 0 }}
                    />
                ) : (
                    <img
                        src="/assets/location.png"
                        alt="location"
                        style={{ width: responsiveConfig.iconSize.flag - 2, height: responsiveConfig.iconSize.flag - 2, flexShrink: 0 }}
                    />
                )}
                <Typography variant="body2" sx={{ fontSize: responsiveConfig.fontSize.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {countryName}
                </Typography>
            </Box>
            {hovered ? (
                <Typography
                    variant="body2"
                    onClick={handleClick}
                    sx={{
                        fontWeight: 'medium',
                        fontSize: responsiveConfig.fontSize.body,
                        color: '#006C9C',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        ml: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        '&:hover': { textDecoration: 'underline' },
                    }}
                    >
                    {t('dashboard.global.visibility.seeST')}
                    <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
                    </Typography>
            ) : (
                <Typography variant="body2" color="rgba(99, 115, 129, 1)" sx={{ fontWeight: 'medium', fontSize: responsiveConfig.fontSize.body, whiteSpace: 'nowrap', ml: 1 }}>
                    {ts}
                </Typography>
            )}
        </Box>
    );
};

export const createDataGridColumns = ({
    responsiveConfig,
    theme,
    t,
    workspace,
    sortModel,
    toggleTrafficShareSort,
    PresenceRateChart,
    columnType = 'country',
    cellMode,
    selectedCountry,
}: DataGridConfigProps): GridColDef[] => {
    // Choisir la première colonne selon le type
    const firstColumn = columnType === 'searchTerm' ? {
        field: 'searchTerm',
        headerName: t('dashboard.global.searchTerm'),
        width: responsiveConfig.searchTermWidth,
        resizable: true,
        disableColumnMenu: true,
        sortable: true,
        headerClassName: 'searchTermHeader',
        valueGetter: (_value: any, row: any) => row?.searchTerm ?? '',
        sortComparator: (v1: any, v2: any, params1: any, params2: any) => {
            const r1 = params1.api.getRow(params1.id);
            const r2 = params2.api.getRow(params2.id);
            return (r1?.trafficShare ?? 0) - (r2?.trafficShare ?? 0);
        },
        renderHeader: (params: any) => (
            <Box sx={{ display: 'flex', flex: '1 1 auto', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: responsiveConfig.minWidth }}>
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.cell, ml: 'auto' }}>
                    <CustomTooltip title={t('dashboard.global.kpiDefinitions.trafficShare') ?? ''} placement="top">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.cell }} onClick={(e: any) => { e.stopPropagation(); toggleTrafficShareSort(); }} aria-label="toggle-sort-traffic-share">
                            <SvgColor
                                src={`/assets/icons/navbar/${theme.palette.mode === 'dark'
                                    ? 'solar--pie-chart-2-bold-duotone-dark-mode'
                                    : 'solar--pie-chart-2-bold-duotone-light-mode'}.svg`}
                                sx={{ width: responsiveConfig.iconSize.chart, height: responsiveConfig.iconSize.chart }}
                            />
                            <IconButton size="small" sx={{ p: 0.25 }}>
                                {sortModel[0]?.field === 'searchTerm'
                                    ? (sortModel[0]?.sort === 'desc'
                                        ? <Iconify icon="iconamoon:arrow-down-2-light" width={responsiveConfig.iconSize.arrow} />
                                        : <Iconify icon="iconamoon:arrow-up-2-light" width={responsiveConfig.iconSize.arrow} />)
                                    : null}
                            </IconButton>
                        </Box>
                    </CustomTooltip>
                </Box>
            </Box>
        ),
        renderCell: (params: any) => (
            <SearchTermCell
                row={params.row}
                responsiveConfig={responsiveConfig}
                cellMode={cellMode}
                selectedCountry={selectedCountry}
                t={t}
            />
        ),
    } : {
        field: 'country',
        headerName: t('dashboard.global.country'),
        width: responsiveConfig.countryWidth,
        disableColumnMenu: true,
        sortable: true,
        valueGetter: (_value: any, row: any) => row?.country ?? '',
        sortComparator: (v1: any, v2: any, params1: any, params2: any) => {
            const r1 = params1.api.getRow(params1.id);
            const r2 = params2.api.getRow(params2.id);
            return (r1?.trafficShare ?? 0) - (r2?.trafficShare ?? 0);
        },
        renderHeader: (params: any) => (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', minWidth: responsiveConfig.minWidth }}>
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.cell }}>
                    <CustomTooltip title={t('dashboard.global.visibility.searchShare') ?? ''} placement="top">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveConfig.spacing.cell }} onClick={(e: any) => { e.stopPropagation(); toggleTrafficShareSort(); }} aria-label="toggle-sort-traffic-share">
                            <SvgColor
                                src={`/assets/icons/navbar/${theme.palette.mode === 'dark'
                                    ? 'solar--pie-chart-2-bold-duotone-dark-mode'
                                    : 'solar--pie-chart-2-bold-duotone-light-mode'}.svg`}
                                sx={{ width: responsiveConfig.iconSize.chart, height: responsiveConfig.iconSize.chart }}
                            />
                            <IconButton size="small" sx={{ p: 0.25 }}>
                                {sortModel[0]?.field === 'country'
                                    ? (sortModel[0]?.sort === 'desc'
                                        ? <Iconify icon="iconamoon:arrow-down-2-light" width={responsiveConfig.iconSize.arrow} />
                                        : <Iconify icon="iconamoon:arrow-up-2-light" width={responsiveConfig.iconSize.arrow} />)
                                    : null}
                            </IconButton>
                        </Box>
                    </CustomTooltip>
                </Box>
            </Box>
        ),
        renderCell: (params: any) => {
            return (
                <CountryCell row={params.row} responsiveConfig={responsiveConfig} t={t} />
            );
        },
    };

    const coverageColumn: GridColDef = {
        field: 'competitionEvolution',
        headerName: t('dashboard.visibility.coverage'),
        width: responsiveConfig.competitionEvolutionWidth,
        sortable: true,
        align: 'left',
        headerAlign: 'left',
        cellClassName: 'leftAlignCell',
        valueGetter: (_value: any, row: any) => {
            const raw = (row?.levelRaw as string) ?? '';
            return raw === 'high' ? 3 : raw === 'active' ? 2 : raw === 'low' ? 1 : 0;
        },
        sortComparator: (v1, v2) => {
            const order = { high: 3, active: 2, low: 1 };
            const a = typeof v1 === 'string' ? order[v1 as keyof typeof order] || 0 : v1;
            const b = typeof v2 === 'string' ? order[v2 as keyof typeof order] || 0 : v2;
            return a - b;
        },
        renderHeader: (params) => (
            <CustomTooltip title={t('dashboard.global.visibility.tooltips.signalCoverage') ?? ''} placement="top">
                <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
            </CustomTooltip>
        ),
        renderCell: (params) => {
            if (params.row?.status === 'not scrapped') return null;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', height: '100%', alignSelf: 'stretch', minWidth: 0, overflow: 'visible' }}>
                    <CompetitionEvolutionCell
                        level={params.row.levelRaw}
                        brandDomain={params.row.brandDomain}
                        country={params.row.country}
                        competitorDomain={params.row.firstCompetitorDomain}
                        brandPresenceRate={params.row.paidPresenceRate}
                        competitorPresenceRate={params.row.firstCompetitorPaidPresenceRate}
                        isDemoBrand={params.row.isDemoBrand}
                    />
                </Box>
            );
        },
    };

    return [
        firstColumn,
        ...(cellMode === 'searchTermAnalysis' ? [] : [coverageColumn]),
        {
            field: 'topCompetitors',
            headerName: t('dashboard.visibility.topDomains'),
            flex: 1,
            minWidth: responsiveConfig.topCompetitorsMinWidth,
            sortable: false,
            cellClassName: 'topCompetitorsCell',
            renderHeader: (params) => (
                <CustomTooltip title={t('dashboard.global.kpiDefinitions.topDomains') ?? ''} placement="top">
                    <span style={{ fontSize: responsiveConfig.fontSize.header, fontWeight: 600, color: '#637381' }}>
                        {params.colDef.headerName}
                    </span>
                </CustomTooltip>
            ),
            renderCell: (params) => {
                if (params.row?.status === 'not scrapped') return null;
                const competitors: string[] = Array.isArray(params.value) ? params.value : [];
                const rates: Record<string, number> = params.row.topCompetitorsPaidRates || {};
                const filteredCompetitors: string[] = competitors.slice(0, 5);
                const first = filteredCompetitors[0];
                if (!first) return null;
                const brandDomain: string | undefined = params.row?.brandDomain;
                const brandRate: number | null = typeof params.row?.paidPresenceRate === 'number' ? Math.round(params.row.paidPresenceRate) : null;
                // Construire le contenu du tooltip unique avec tous les concurrents
                const tooltipContent = (
                    <Box sx={{ minWidth: 280 }}>
                        <Stack direction="column" spacing={1.25}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ px: 1.5, pt: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: responsiveConfig.fontSize.caption
                                    }}>
                                    {'Competitors'}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: responsiveConfig.fontSize.caption
                                    }}>
                                    Paid PR
                                </Typography>
                            </Stack>
                            <Stack direction="column" spacing={0.5}>
                                {brandDomain ? (
                                    <Stack
                                        key={`brand-${brandDomain}`}
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{
                                            px: 1.5,
                                            py: 0.75,
                                            borderRadius: 1.5,
                                            backgroundColor: theme.palette.mode === 'dark' ? "rgba(145, 158, 171,0.20)" : alpha('#00A76F', 0.1),
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                            <Box
                                                component="img"
                                                src={domainFavicon(brandDomain)}
                                                sx={{
                                                    width: responsiveConfig.iconSize.domain,
                                                    height: responsiveConfig.iconSize.domain,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '50%',
                                                    display: 'block',
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                noWrap
                                                sx={{
                                                    fontSize: responsiveConfig.fontSize.caption,
                                                    maxWidth: 180,
                                                    fontWeight: 500
                                                }}>
                                                {brandDomain}
                                            </Typography>
                                        </Stack>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: responsiveConfig.fontSize.caption }}>
                                            {brandRate != null ? `${brandRate}%` : '-'}
                                        </Typography>
                                    </Stack>
                                ) : null}
                                {filteredCompetitors.map((competitor: string, index: number) => {
                                    const rate = typeof rates[competitor] === 'number' ? rates[competitor] : null;
                                    return (
                                        <Stack
                                            key={competitor}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{
                                                px: 1.5,
                                                py: 0.75,
                                                borderRadius: 1.5,
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                                <Box
                                                    component="img"
                                                    src={domainFavicon(competitor)}
                                                    sx={{
                                                        width: responsiveConfig.iconSize.domain,
                                                        height: responsiveConfig.iconSize.domain,
                                                        backgroundColor: '#fff',
                                                        borderRadius: '50%',
                                                        display: 'block',
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    noWrap
                                                    sx={{ fontSize: responsiveConfig.fontSize.caption, maxWidth: 180, fontWeight: 500 }}>
                                                    {competitor}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: responsiveConfig.fontSize.caption }}>
                                                {rate != null ? (Math.round(rate) === 0 ? '<1%' : `${Math.round(rate)}%`) : '-'}
                                            </Typography>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Stack>
                    </Box>
                );

                const cellCountryName = params.row?.country || selectedCountry;
                return (
                    <CustomTooltip title={tooltipContent} placement="top">
                        <Box component="span" sx={{ display: 'inline-flex', width: '100%' }}>
                            <TopCompetitorsCell
                                competitors={filteredCompetitors}
                                rates={rates}
                                responsiveConfig={responsiveConfig}
                                countryName={cellCountryName}
                                t={t}
                            />
                        </Box>
                    </CustomTooltip>
                );
            },
        },
        {
            field: 'blendedPresenceRate',
            headerName: t('dashboard.global.visibility.blendedVisibilityRate'),
            flex: 1,
            minWidth: responsiveConfig.blendedPresenceMinWidth,
            renderHeader: (params) => (
                <CustomTooltip title={t('dashboard.global.kpiDefinitions.blendedPresenceRate') ?? ''} placement="top">
                    <span style={{ fontSize: responsiveConfig.fontSize.header }}>{params.colDef.headerName}</span>
                </CustomTooltip>
            ),
            renderCell: (params) => {
                if (params.row?.status === 'not scrapped') return null;
                return (
                    <VisibilityRateCell
                        row={params.row}
                        responsiveConfig={responsiveConfig}
                        PresenceRateChart={PresenceRateChart}
                        selectedCountry={selectedCountry}
                        columnType={columnType}
                        t={t}
                    />
                );
            },
        },
    ];
};
